import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { TESTIDS } from "@/constants/testIds";
import { Share2, RotateCcw, Eye, Home } from "lucide-react";

const REASONS = {
  ALL_OPPONENT_COUNTERS_CAPTURED: "All opposing counters captured",
  OPPONENT_NO_LEGAL_MOVE: "Opponent has no legal move",
};

export function ResultDialog({
  open,
  state,
  rulesetName,
  mode,
  humanSide,
  onRematch,
  onReview,
  onHome,
  durationMs,
}) {
  const winnerLabel = (() => {
    if (!state.winner) return "Draw";
    if (mode === "single") {
      return state.winner === humanSide ? "You win" : "Computer wins";
    }
    return state.winner === "A" ? "Player 1 wins" : "Player 2 wins";
  })();

  const captured =
    (state.captures.A || 0) + (state.captures.B || 0);
  const moves = state.moveNumber;
  const turns = state.turnNumber;
  const largest = state.history?.reduce((m, h) => Math.max(m, h.captured || 0), 0) || 0;

  const handleShare = async () => {
    const url = "https://playtsoro.com/";
    const text = `I just played ${rulesetName} on Play Tsoro. Result: ${winnerLabel}. Play at ${url}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Play Tsoro", text, url });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(text);
      }
    } catch (e) {
      // ignore
    }
  };

  return (
    <Dialog open={open}>
      <DialogContent data-testid={TESTIDS.resultDialog} className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle data-testid={TESTIDS.resultWinner} className="font-display text-3xl">
            {winnerLabel}
          </DialogTitle>
          <DialogDescription data-testid={TESTIDS.resultReason}>
            {REASONS[state.endReason] || state.endReason || "Game ended."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <Stat label="Ruleset" value={rulesetName} />
          <Stat label="Region" value="Zimbabwe" />
          <Stat label="Captured" value={captured} mono />
          <Stat label="Largest capture" value={largest} mono />
          <Stat label="Turns" value={turns} mono />
          <Stat label="Moves" value={moves} mono />
          {durationMs ? (
            <Stat label="Duration" value={formatDuration(durationMs)} mono />
          ) : null}
        </div>

        <DialogFooter className="flex flex-wrap gap-2 sm:gap-2">
          <Button data-testid={TESTIDS.rematchBtn} onClick={onRematch} className="rounded-full">
            <RotateCcw className="h-4 w-4 mr-2" /> Rematch
          </Button>
          <Button
            data-testid={TESTIDS.reviewBtn + "-result"}
            variant="outline"
            onClick={onReview}
            className="rounded-full"
          >
            <Eye className="h-4 w-4 mr-2" /> Review
          </Button>
          <Button
            data-testid={TESTIDS.shareBtn}
            variant="outline"
            onClick={handleShare}
            className="rounded-full"
          >
            <Share2 className="h-4 w-4 mr-2" /> Share
          </Button>
          <Button data-testid={TESTIDS.homeBtn + "-result"} variant="ghost" onClick={onHome} className="rounded-full">
            <Home className="h-4 w-4 mr-2" /> Home
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, mono }) {
  return (
    <div className="rounded-lg bg-muted/50 px-3 py-2">
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className={mono ? "font-mono font-semibold" : "font-medium"}>{value}</p>
    </div>
  );
}

function formatDuration(ms) {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}m ${rem}s`;
}
